(function (angular) {
    'use strict';
    const app = angular.module('enervee');

    /**
     * Provides methods for retrieving product data from the Swiftype, Rebate, and Checkout apis,
     * and then formats the data for use in the product landing page.
     *
     */
    app.factory('ProductLandingPageService', [
        '$q',
        '$state',
        'UtilService',
        'SwiftypeService',
        'RebatesService',
        'CategoriesService',
        'EnerveeCheckoutService',
        function (
            $q,
            $state,
            UtilService,
            SwiftypeService,
            RebatesService,
            CategoriesService,
            EnerveeCheckoutService,
        ) {
            const _pub = {
                PRODUCTS: 'products',
                FEATURED: 'featured',
                productListFromURL: productListFromURL,
                addToCategoryMap: addToCategoryMap,
                fetchProductData: fetchProductData,
                assembleProductData: assembleProductData,
                createLandingPageUrl: createLandingPageUrl,
                createShareLinkFromProducts: createShareLinkFromProducts,
            }

            return _pub;

            /**
             * Coerces data from the URL into a shape similar to the configuration specified in the
             * Settings Service, returning an array of objects containing a product ID, a category code,
             * and a label.
             *
             * @param {Object} searchObject - An object containing data retrieved from the query params
             *     of the url.
             * @param {String} configName - String describing which product type to take from the query params.
             * @returns {Object[]} Returns an array of objects with three properties, product_id, category,
             *     and label.
             */
            function productListFromURL(searchObject, configName) {
                if (searchObject.category) {
                    if (searchObject.pid && configName === 'products') {
                        return createProductConfigFromURL(searchObject, 'pid');
                    }
                    if (searchObject.featuredpid && configName === 'featured') {
                        return createProductConfigFromURL(searchObject, 'featuredpid');
                    }
                }

                return [];
            }

            /**
             * Pushes the product_id in the given productConfig to the correct property in the
             * categoryMap. Returns the categoryMap with the modification made.
             *
             * @param {Object} categoryMap - The map of cEnerveeCheckoutServiceategories and their products to fetch.
             *     The keys are category IDs, and the values are a arrays of product IDs.
             *     THIS IS MODIFIED IN PLACE.
             * @param {Object} productConfig - An object containing the product configuration.
             *     Contains properties for the product ID, its category code, and a label for
             *     the product.
             * @returns {Object} The modified categoryMap provided. Note that the object is modified
             *     and is not copied.
             */
            function addToCategoryMap(categoryMap, productConfig) {
                categoryMap[productConfig.category_code] = categoryMap[productConfig.category_code] || [];
                categoryMap[productConfig.category_code].push(productConfig.product_id);
                return categoryMap;
            }

            /**
             * Retrieves data for the provided products from Swiftype, the Rebates API, and the
             * Checkout API, returning a single promise that resolves with the data from all
             * three services.
             *
             * @param {Object} productIdsByCategoryCode - The map of categories and their products
             *     to fetch. The keys are category IDs, and the values are an array of product IDs.
             * @returns {Promise<[Object, Object, *]>} An array with three values. The values
             *     returned are in this order:
             *       * An object, where the keys are product IDs and the values are the product data.
             *       * An object, where the keys are a product ID and the values are the rebate data.
             *         Defaults to an empty object if the promise rejects.
             *       * A collection of checkout data that can be applied to the products. Defaults
             *         to null if the promise rejects.
             *
             */
            function fetchProductData(productIdsByCategoryCode) {
                const productIds = UtilService.objectValues(productIdsByCategoryCode)
                    .reduce((acc, products) => acc.concat(products), []);
                const swiftypePromise = SwiftypeService.getProducts(productIdsByCategoryCode, false);
                const rebatesPromise = RebatesService.getRebatesForProducts(productIds).catch(() => ({}));
                const checkoutPromise = CategoriesService.afterInitialCategoriesLoads().then(() => {
                    // Create a request to Checkout for each category on the page, and default that request to
                    // an empty array if anything goes wrong.
                    const checkoutRequests = UtilService.objectEntries(productIdsByCategoryCode)
                        .map(([categoryCode, productIds]) => {
                            const categoryId = CategoriesService.getCategoryIdByCode(categoryCode);
                            if (!CategoriesService.getCategoryById(categoryId).is_checkout_enabled) {
                                return [];
                            }
                            return EnerveeCheckoutService.fetchCheckoutProducts(categoryId, productIds).catch(() => []);
                        });
                    return $q.all(checkoutRequests).then(results =>
                        results.reduce((acc, result) => acc.concat(result), [])
                    );
                });

                return $q.all([swiftypePromise, rebatesPromise, checkoutPromise]);
            }

            /**
             * Takes product information, rebate information, and checkout information, and combines
             * them all into a list of products that include all necessary data.
             *
             * @param {Object} products - A map of products where the keys are the product IDs.
             * @param {Object} rebates - A map of rebates where the keys are the product IDs.
             * @param {Object[]} checkoutData - Data retrieved from the Checkout API.
             * @param {Object[]} productsConfig - An array or product data, used for the category code.
             * @returns {Object[]} An array with products with all the provided data combined.
             *
             */
            function assembleProductData(products, rebates, checkoutData, productsConfig) {
                // Rebates must be added to product before checkout data
                const productsWithRebateData = UtilService.objectValues(products).map(product => {
                    product.rebates = rebates[product.id];
                    return product;
                });
                if (checkoutData) {
                    EnerveeCheckoutService.applyCheckoutDataToProducts(productsWithRebateData, checkoutData);
                }
                productsWithRebateData.forEach(product => {
                    // This should run synchronously, but if not, there shouldn't be any noticeable issues
                    // in the final rendering.
                    const categoryCode = productsConfig.find(item => item.product_id === product.id).category_code;
                    CategoriesService.getCategoryByCode(categoryCode).then(category => {
                        product.display_title = product.brand;
                        product.display_description = product.model_number;
                        if (category.show_model_number) {
                            product.display_title = `${product.brand} ${product.model_number}`;
                            product.display_description = product.display_name.replace(
                                product.brand,
                                ''
                            );
                        }
                    });

                    product.msrp = product.checkoutMSRP || parseFloat(product.price);
                    const totalRebatesAmount = product.rebates
                        .filter(rebate => rebate.amount && rebate.highestInType)
                        .reduce((total, rebate) => total + parseFloat(rebate.amount), 0);
                    product.priceAfterDiscounts =
                        product.msrp
                        - totalRebatesAmount
                        - (product.checkoutInfo ? product.checkoutInfo.total_additional_discounts : 0);
                });
                return productsWithRebateData;
            }

            /**
             * Used to created a product configuration array from the url search object.
             *
             * @param {Object} searchObject - Object from the url
             * @param {string} pidName - String used to identify which PID to get from the query params.
             * @return {Object[]} Array of products with basic configuration data.
             */
            function createProductConfigFromURL(searchObject, pidName) {
                let products = processQueryParam(searchObject[pidName]);
                let categories = processQueryParam(searchObject.category);
                return products
                    .map(pid => parseInt(pid, 10))
                    .map((productId, idx) => ({
                        product_id: productId,
                        // if multiple categories exist, then the index is used to get the right one.
                        category_code: categories.length > 1 ? categories[idx] : categories[0],
                        label: undefined,
                    }));
            }

            /**
             * Create share link for social media directive.
             *
             * @param {Object[]} products - Products in order.
             * @return {string} Full url to be shared.
             */
            function createShareLinkFromProducts(products) {
                const sharePageSlug = UtilService.getSiteSetting('social_media_sharing').share_page_slug;
                if (products.length > 1) {
                    return createLandingPageUrl(products, sharePageSlug);
                } else {
                    return createProductPageLink(products[0]);
                };
            }

            /**
             * Creates a product page url with context from product data.
             * Product must be an object with a `id` and `category_code` properties.
             * @example: { id: 12345678, category_code: 1 }
             *
             * @param {Object[]} products - Array of products.
             * @return {string} Full url.
             */
            function createProductPageLink(product) {
                const categoryCode = getCategoryCode(product.category_id);
                return $state.href(
                    'product',
                    { category_code: categoryCode, product_info: product.id},
                    { absolute: true }
                );
            }

            /**
             * Used for splitting a string containing multiple values
             * for the query parameter.  If the query param is already an
             * area, it does not get split.
             *
             * @param {string|Array} queryParam - The query param to be processed.
             * @return {Array} Array of values.
             */
            function processQueryParam(queryParam) {
                if (!UtilService.isIterable(queryParam)) {
                    return queryParam.split(',');
                }
                return queryParam;
            }

            /**
             * Creates a landing page url dynamically from an array or products.
             * Products must be an object with an `id` and `category_code` properties.
             * @example: [ { id: 12345678, category_code: 1 } ]
             *
             * @param {Object[]} products - Array of products.
             * @param {string} slug - Slug for CMS page.
             * @return {string} Full url.
             */
            function createLandingPageUrl(products, slug) {
                const productIds = [];
                const categoryCodes = [];
                products.forEach(product => {
                    productIds.push(product.id.toString());
                    categoryCodes.push(getCategoryCode(product.category_id));
                });
                const queryParams = UtilService.formatQueryParams({
                    pid: productIds.join(","),
                    category: categoryCodes.join(","),
                });
                const path = $state.href(
                    'flat-pages',
                    { path: slug },
                    { absolute: true }
                );
                return UtilService.addQueryParamToURL(path, queryParams);
            }

            /**
             * Gets the category code for the given category ID.
             *
             * @param {integer} category_id
             * @return {string} Category code.
             */
            function getCategoryCode(category_id) {
                return CategoriesService.getCategoryById(category_id).code;
            }
        }
    ]);
})(angular);
